import Image from 'next/image';
import type { ReactNode } from 'react';

import { cx } from '@/shared/lib/cx';

import styles from './media-frame.module.scss';

const HEX = /^#[\da-f]{3,8}$/i;
const POSITION =
  /^(?:center|top|bottom|left|right|[\d.]+%)(?: (?:center|top|bottom|left|right|[\d.]+%))?$/;
const PERCENT = /^\d{1,3}(?:\.\d+)?%$/;

export type MediaFrameProps = {
  src: string | null;
  alt: string;
  /** "sketch" — line drawing at natural size, max 55% of the frame, multiply blending (design ".photo.sketch"). */
  kind?: 'photo' | 'sketch';
  position?: string | null;
  /** Sketch drawn scaled to this share of the frame ("62%" in cart rows, "70%" in account) instead of its natural size. */
  sketchSize?: string | null;
  /** Natural size of a sketch image; photos fill the frame. */
  width?: number | null;
  height?: number | null;
  sizes: string;
  priority?: boolean;
  /** Placeholder when there is no image: "hatch" — PDP ".ph", "plain" — home ".dirImg", "dashed" — home ".ph". */
  /** `icon` sits above the label (the play icon of the video placeholder). */
  placeholder?: { label: ReactNode; sub?: ReactNode; icon?: ReactNode } | undefined;
  placeholderStyle?: 'hatch' | 'plain' | 'dashed';
  tint?: string | null;
  /** "contain" — packshots on white (gift set). */
  fit?: 'cover' | 'contain';
  className?: string | undefined;
  children?: ReactNode;
};

/** Photo frame of the design: background, cover photo or sketch, overlay slots (badge, tag). No rounded corners. */
export function MediaFrame({
  src,
  alt,
  kind = 'photo',
  position = null,
  sketchSize = null,
  width = null,
  height = null,
  sizes,
  priority = false,
  placeholder,
  placeholderStyle = 'hatch',
  tint = null,
  fit = 'cover',
  className,
  children,
}: MediaFrameProps) {
  const style = tint && HEX.test(tint) ? { '--media-tint': tint } : undefined;
  const imageStyle = position && POSITION.test(position) ? { objectPosition: position } : undefined;
  const sketch = src && kind === 'sketch';
  const scaled = sketchSize && PERCENT.test(sketchSize) ? sketchSize : null;
  const natural = sketch && !scaled && width && height;
  return (
    <div
      className={cx(styles.root, className)}
      style={style}
      data-kind={src ? kind : 'placeholder'}
      data-placeholder={src ? undefined : placeholderStyle}
    >
      {natural && (
        <Image
          src={src}
          alt={alt}
          width={width}
          height={height}
          sizes={sizes}
          className={styles.sketch}
          priority={priority}
        />
      )}
      {sketch && !natural && (
        <span className={styles.box} style={scaled ? { '--sketch-box': scaled } : undefined}>
          <Image
            src={src}
            alt={alt}
            fill
            sizes={sizes}
            className={styles.contain}
            style={imageStyle}
            priority={priority}
          />
        </span>
      )}
      {src && kind === 'photo' && (
        <Image
          src={src}
          alt={alt}
          fill
          sizes={sizes}
          className={fit === 'contain' ? styles.contain : styles.cover}
          style={imageStyle}
          priority={priority}
        />
      )}
      {!src && (
        <span
          className={styles.placeholder}
          role={alt ? 'img' : undefined}
          aria-label={alt || undefined}
        >
          {placeholder?.icon}
          {placeholder && <b className={styles.label}>{placeholder.label}</b>}
          {placeholder?.sub && <span className={styles.sub}>{placeholder.sub}</span>}
        </span>
      )}
      {children}
    </div>
  );
}
